import createCachedSelector from 're-reselect';

import { isEditPublisher, isReplyPublisher } from '../../state/publishers/types';
import type { Publisher, User, YammerState } from '../../state/types';
import { getParticipantIdsFromMessageWithSender } from '../messageParticipants/selectors';
import { getFulfilledMessages, getSenderReferenceForMessage } from '../messages/messageSelectors';
import { findByIdThrows as findSecondLevelRepliesByIdThrows } from '../secondLevelReplies/selectors';
import { findByIdThrows as findThreadByIdThrows, getFulfilledThreads } from '../threads/findThreadSelectors';
import { findById as findTopLevelRepliesById } from '../topLevelReplies/selectors';
import { isExternal } from '../users/selectors';

import { findByIdThrows } from './findPublisherSelectors';
import { isEditingDirectMessage } from './publisherDestinationSelectors';
import { isDirectReplyPublisher } from './publisherTypeSelectors';
import { getReplyingToUser } from './publishersSelectors';

const emptyParticipantIds: User['id'][] = [];

type GetExistingThreadParticipants = (state: YammerState, publisherId: Publisher['id']) => User['id'][];
export const getExistingThreadParticipants: GetExistingThreadParticipants = createCachedSelector(
  findByIdThrows,
  getFulfilledThreads,
  getFulfilledMessages,
  (publisher, fulfilledThreads, fulfilledMessages) => {
    if (isReplyPublisher(publisher)) {
      const { participantIds } = fulfilledThreads[publisher.threadId];

      return participantIds;
    }

    if (isEditPublisher(publisher)) {
      const { threadId } = fulfilledMessages[publisher.messageId];
      const { participantIds } = fulfilledThreads[threadId];

      return participantIds;
    }

    return emptyParticipantIds;
  }
)((_state, id) => id);

type HasAddedNewNotifiedUsers = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasAddedNewNotifiedUsers: HasAddedNewNotifiedUsers = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (isEditingDirectMessage(state, publisherId) || isDirectReplyPublisher(state, publisherId)) {
    const participantIds = getExistingThreadParticipants(state, publisherId);

    return publisher.notifiedUserIds.some((userId) => !participantIds.includes(userId));
  }

  return false;
};

type HasExternalThreadParticipants = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const hasExternalThreadParticipants: HasExternalThreadParticipants = (state, publisherId) =>
  getExistingThreadParticipants(state, publisherId).some((userId) => isExternal(state, userId));

type GetAllParticipantIdsFromThread = (state: YammerState, publisherId: Publisher['id']) => (User['id'] | undefined)[];
/**
 * @deprecated PERFORMANCE This selector returns a new array on every render, it must be cached
 */
export const getAllParticipantIdsFromThread: GetAllParticipantIdsFromThread = (state, publisherId) => {
  let participants: (User['id'] | undefined)[] = [];

  const publisher = findByIdThrows(state, publisherId);

  if (!isReplyPublisher(publisher)) {
    return participants;
  }

  const thread = findThreadByIdThrows(state, publisher.threadId);
  participants = getParticipantIdsFromMessageWithSender(state, thread.threadStarterId);

  const topLevelReplies = findTopLevelRepliesById(state, thread.id);
  if (topLevelReplies === undefined) {
    return participants;
  }

  for (const topLevelReplyEdge of topLevelReplies.edges) {
    const topLevelReplyEdgeId = topLevelReplyEdge.nodeId;
    const topLevelReplyParticipants = getParticipantIdsFromMessageWithSender(state, topLevelReplyEdgeId);
    participants = [...participants, ...topLevelReplyParticipants];

    const secondLevelReplies = findSecondLevelRepliesByIdThrows(state, topLevelReplyEdgeId);
    for (const secondLevelReplyEdge of secondLevelReplies.edges) {
      const secondLevelReplyParticipants = getParticipantIdsFromMessageWithSender(state, secondLevelReplyEdge.nodeId);
      participants = [...participants, ...secondLevelReplyParticipants];
    }
  }

  return participants;
};

type GetAllPossibleParticipantIds = (state: YammerState, publisherId: Publisher['id']) => (User['id'] | undefined)[];
/**
 * @deprecated PERFORMANCE This selector returns a new array on every render, it must be cached
 */
export const getAllPossibleParticipantIds: GetAllPossibleParticipantIds = (state, publisherId) => {
  let replyUserIds: (User['id'] | undefined)[] = [];

  const publisher = findByIdThrows(state, publisherId);
  if (isReplyPublisher(publisher)) {
    const threadStarterId = findThreadByIdThrows(state, publisher.threadId).threadStarterId;
    const repliedToUserId = getReplyingToUser(state, publisherId)?.id;
    const threadStarterUserRef = getSenderReferenceForMessage(state, threadStarterId);

    replyUserIds = [repliedToUserId];

    if (threadStarterUserRef.schema === 'users') {
      const threadStarterUserId = threadStarterUserRef.id;
      replyUserIds = [...replyUserIds, threadStarterUserId];
    }
  }

  const directMessageParticipantIds = getExistingThreadParticipants(state, publisherId);
  const otherThreadParticipantIds = getAllParticipantIdsFromThread(state, publisherId);

  const candidateIds = [...replyUserIds, ...directMessageParticipantIds, ...otherThreadParticipantIds];

  return candidateIds;
};
